// =============================================================
// Hooks for the two WebSocket connections:
//   - /ws        : status updates (context %, online/busy)
//   - /ws/chat   : chat history, new messages, typing
// =============================================================

import { useEffect, useRef, useState } from 'react'
import { authWsUrl } from './auth'
import type {
  ChatMessage,
  ChatWsMsg,
  StatusPayload,
  StatusWsMsg,
} from './types'

// ----- /ws (status) -----

export function useStatusSocket(): {
  status: StatusPayload | null
} {
  const [status, setStatus] = useState<StatusPayload | null>(null)
  const wsRef = useRef<WebSocket | null>(null)

  useEffect(() => {
    let cancelled = false
    let timer: number | null = null

    const connect = () => {
      if (cancelled) return
      const proto = location.protocol === 'https:' ? 'wss:' : 'ws:'
      const url = authWsUrl(`${proto}//${location.host}/ws`)
      const ws = new WebSocket(url)
      wsRef.current = ws
      ws.onmessage = (e) => {
        try {
          const msg = JSON.parse(e.data) as StatusWsMsg
          if (msg.type === 'status') setStatus(msg.data)
        } catch {
          // ignore malformed
        }
      }
      ws.onclose = () => {
        if (cancelled) return
        timer = window.setTimeout(connect, 5000)
      }
    }

    connect()
    return () => {
      cancelled = true
      if (timer) window.clearTimeout(timer)
      wsRef.current?.close()
    }
  }, [])

  return { status }
}

// ----- /ws/chat -----

interface UseChatSocketOpts {
  /** Called once we receive the initial history. */
  onHistory?: (messages: ChatMessage[]) => void
  /** Called whenever a new chat message arrives (server-pushed). */
  onMessage?: (m: ChatMessage) => void
  /** Called when cc_typing state changes. */
  onTyping?: (state: 'thinking' | 'tool' | '', tool?: string) => void
  /** Called when cc_status changes (alive indicator). */
  onCcStatus?: (alive: boolean) => void
}

export function useChatSocket(opts: UseChatSocketOpts) {
  const optsRef = useRef(opts)
  optsRef.current = opts
  const wsRef = useRef<WebSocket | null>(null)
  const pingTimerRef = useRef<number | null>(null)
  const seenIdsRef = useRef<Set<string>>(new Set())

  /** Add an id to the dedupe set (exposed for chatSend acknowledgement). */
  const markSeen = (id?: string) => {
    if (id) seenIdsRef.current.add(id)
  }

  /** Reconnect-on-mount, with 5s backoff and a 25s ping heartbeat. */
  useEffect(() => {
    let cancelled = false
    let backoff: number | null = null

    const connect = () => {
      if (cancelled) return
      const proto = location.protocol === 'https:' ? 'wss:' : 'ws:'
      const url = authWsUrl(`${proto}//${location.host}/ws/chat`)
      const ws = new WebSocket(url)
      wsRef.current = ws

      ws.onopen = () => {
        optsRef.current.onCcStatus?.(true)
        if (pingTimerRef.current) window.clearInterval(pingTimerRef.current)
        pingTimerRef.current = window.setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'ping' }))
          }
        }, 25000)
      }

      ws.onmessage = (e) => {
        let msg: ChatWsMsg
        try {
          msg = JSON.parse(e.data) as ChatWsMsg
        } catch {
          return
        }
        switch (msg.type) {
          case 'cc_status':
            optsRef.current.onCcStatus?.(!!(msg as { alive?: boolean }).alive)
            break
          case 'history':
            optsRef.current.onHistory?.((msg as { messages: ChatMessage[] }).messages || [])
            break
          case 'message': {
            const m = msg as ChatMessage & { type: string }
            if (m.id && seenIdsRef.current.has(m.id)) break
            if (m.id) seenIdsRef.current.add(m.id)
            optsRef.current.onMessage?.({
              role: m.role,
              content: m.content,
              ts: m.ts,
              thinking: m.thinking,
              id: m.id,
            })
            break
          }
          case 'sent': {
            const m = msg as { id?: string }
            if (m.id) seenIdsRef.current.add(m.id)
            break
          }
          case 'cc_typing': {
            const t = msg as { state?: 'thinking' | 'tool' | ''; tool?: string }
            optsRef.current.onTyping?.(t.state || '', t.tool)
            break
          }
          case 'pong':
            break
          default:
            break
        }
      }

      ws.onclose = () => {
        optsRef.current.onCcStatus?.(false)
        if (pingTimerRef.current) {
          window.clearInterval(pingTimerRef.current)
          pingTimerRef.current = null
        }
        if (cancelled) return
        backoff = window.setTimeout(connect, 5000)
      }
    }

    connect()
    return () => {
      cancelled = true
      if (backoff) window.clearTimeout(backoff)
      if (pingTimerRef.current) window.clearInterval(pingTimerRef.current)
      wsRef.current?.close()
    }
  }, [])

  return { markSeen }
}